import React from "react";

import { useTranslation } from "utils";

import { ScenarioAnalysisTab } from "consts";

import { useCmBreakdownFlag } from "pages/app/scenario-analysis/hooks/useCmBreakdownFlag";

export type { ScenarioAnalysisTab };

export type ScenarioAnalysisTabFlags = Partial<Record<ScenarioAnalysisTab, boolean>>;

export interface ScenarioAnalysisTabBarProps {
  activeTab: ScenarioAnalysisTab;
  onChange: (tab: ScenarioAnalysisTab) => void;
  changeDots?: ScenarioAnalysisTabFlags;
  dataTestId?: string;
}

const TABS: ReadonlyArray<{ key: ScenarioAnalysisTab; labelKey: string; cmBreakdownLabelKey?: string }> = [
  { key: "scenarios", labelKey: "scenarioTabs.scenarios" },
  { key: "worth-it", labelKey: "scenarioTabs.worthIt" },
  { key: "detailed-calcs", labelKey: "scenarioTabs.detailedCalculations", cmBreakdownLabelKey: "scenarioTabs.financialBreakdown" },
];

export const tabId = (tab: ScenarioAnalysisTab) => `scenario-analysis-tab-${tab}`;
export const tabPanelId = (tab: ScenarioAnalysisTab) => `scenario-analysis-panel-${tab}`;

/** Arrow keys move between the tabs and select each one, as the ARIA tabs pattern specifies. */
const nextTabIndex = (key: string, current: number): number | null => {
  if (key === "ArrowRight") return (current + 1) % TABS.length;
  if (key === "ArrowLeft") return (current - 1 + TABS.length) % TABS.length;
  if (key === "Home") return 0;
  if (key === "End") return TABS.length - 1;
  return null;
};

export const ScenarioAnalysisTabBar = ({ activeTab, onChange, changeDots, dataTestId = "scenario-analysis-tabs" }: ScenarioAnalysisTabBarProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const isCmBreakdownEnabled = useCmBreakdownFlag();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = nextTabIndex(
      event.key,
      TABS.findIndex(({ key }) => key === activeTab)
    );
    if (target === null) return;
    event.preventDefault();
    const nextTab = TABS[target].key;
    onChange(nextTab);
    document.getElementById(tabId(nextTab))?.focus();
  };

  return (
    <div className="scenario-analysis-tab-bar" role="tablist" onKeyDown={handleKeyDown} data-testid={dataTestId}>
      {TABS.map(({ key, labelKey, cmBreakdownLabelKey }) => {
        const isActive = key === activeTab;
        const label = isCmBreakdownEnabled && cmBreakdownLabelKey ? cmBreakdownLabelKey : labelKey;
        return (
          <button
            key={key}
            id={tabId(key)}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={tabPanelId(key)}
            // Roving tabindex: only the selected tab is a tab stop. The arrow keys reach the others.
            tabIndex={isActive ? 0 : -1}
            className={`scenario-analysis-tab${isActive ? " scenario-analysis-tab--active" : ""}`}
            onClick={() => onChange(key)}
            data-testid={`${dataTestId}-${key}`}
          >
            {t(label)}
            {changeDots?.[key] ? (
              <span className="scenario-analysis-tab-dot" title={t("scenarioTabs.changed")} data-testid={`${dataTestId}-${key}-dot`} />
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default ScenarioAnalysisTabBar;
